import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, getErrorMessage } from "@/lib/queryClient";
import { apiUrl } from "@/lib/api";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, Users, MapPin, Monitor, ChevronRight, Archive, RotateCcw, Trash2 } from "lucide-react";
import type { Student } from "@contracts/student";
import type { TeacherProfile } from "@contracts/teacher";
import StudentFormDialog from "@/components/student-form-dialog";
import ConfirmDialog from "@/components/confirm-dialog";
import UndoDeleteBanner from "@/components/undo-delete-banner";

export default function StudentsPage() {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<"all" | "online" | "offline">("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState<Student | null>(null);
  const [confirmPermDelete, setConfirmPermDelete] = useState<Student | null>(null);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const pendingDeleteRef = useRef<{ timer: ReturnType<typeof setTimeout> } | null>(null);
  const [pendingDeleteUi, setPendingDeleteUi] = useState<{
    message: string;
    deadline: number;
    durationMs: number;
  } | null>(null);

  const { data: students = [], isLoading } = useQuery<Student[]>({
    queryKey: ["/api/students"],
  });

  const { data: deletedStudents = [] } = useQuery<Student[]>({
    queryKey: ["/api/students", "deleted"],
    queryFn: async () => {
      const res = await fetch(apiUrl("/api/students/deleted"), { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/students/${id}/restore`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      toast({ title: "Student restored", variant: "success" });
      setConfirmRestore(null);
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Failed to restore student"), variant: "destructive" });
    },
  });

  const permanentDeleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/students/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      toast({ title: "Student permanently deleted", variant: "success" });
      setConfirmPermDelete(null);
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Failed to delete student"), variant: "destructive" });
    },
  });

  const filtered = students.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === "all" || s.lessonType === filterType;
    return matchesSearch && matchesType;
  });

  useEffect(() => {
    return () => {
      if (pendingDeleteRef.current) clearTimeout(pendingDeleteRef.current.timer);
      setPendingDeleteUi(null);
    };
  }, []);

  function cancelPendingDelete() {
    if (!pendingDeleteRef.current) return;
    clearTimeout(pendingDeleteRef.current.timer);
    pendingDeleteRef.current = null;
    setPendingDeleteUi(null);
  }

  function queuePermanentDelete(id: string) {
    if (pendingDeleteRef.current) {
      clearTimeout(pendingDeleteRef.current.timer);
    }
    const timer = setTimeout(() => {
      pendingDeleteRef.current = null;
      setPendingDeleteUi(null);
      permanentDeleteMutation.mutate(id);
    }, 10_000);
    pendingDeleteRef.current = { timer };
    setPendingDeleteUi({
      message: "Deleting student in 10 seconds",
      deadline: Date.now() + 10_000,
      durationMs: 10_000,
    });
    setConfirmPermDelete(null);
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-3">
          <Skeleton className="h-9 flex-1 max-w-sm" />
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-32" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6 max-w-7xl mx-auto" data-testid="page-students">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" data-testid="text-page-title">Students</h1>
          <p className="text-muted-foreground text-sm mt-1">{students.length} student{students.length !== 1 ? "s" : ""}</p>
        </div>
        <StudentFormDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          trigger={
            <Button data-testid="button-add-student">
              <Plus className="w-4 h-4 mr-2" />
              Add Student
            </Button>
          }
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search students..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-search-students"
          />
        </div>
        <Select value={filterType} onValueChange={(v) => setFilterType(v as any)}>
          <SelectTrigger className="w-36" data-testid="select-filter-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="py-16 text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="font-medium mb-1">{students.length === 0 ? "No students yet" : "No results found"}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {students.length === 0
              ? "Add your first student to get started"
              : "Try adjusting your search or filters"}
          </p>
          {students.length === 0 && (
            <Button onClick={() => setShowAddDialog(true)} data-testid="button-add-first-student">
              <Plus className="w-4 h-4 mr-2" />
              Add Student
            </Button>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((student) => (
            <Card
              key={student.id}
              className="p-5 cursor-pointer hover-elevate"
              onClick={() => navigate(`/students/${student.id}`)}
              data-testid={`card-student-${student.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm flex-shrink-0">
                  {student.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm truncate">{student.name}</h3>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    <Badge variant={student.lessonType === "online" ? "default" : "secondary"}>
                      {student.lessonType === "online" ? (
                        <><Monitor className="w-3 h-3 mr-1" />Online</>
                      ) : (
                        <><MapPin className="w-3 h-3 mr-1" />Offline</>
                      )}
                    </Badge>
                    <Badge variant="outline">{student.gender}</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">
                      {student.unitRate} {student.unitCurrency}/hr
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {student.lessonDuration}min lessons
                    </span>
                  </div>
                  {student.lessonType === "offline" && student.address && (
                    <p className="text-xs text-muted-foreground mt-2 truncate flex items-center gap-1">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      {student.address}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {deletedStudents.length > 0 && (
        <div className="pt-4 border-t">
          <button
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
            onClick={() => setShowArchived(!showArchived)}
            data-testid="button-toggle-archived"
          >
            <Archive className="w-4 h-4" />
            Archived Students ({deletedStudents.length})
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showArchived ? "rotate-90" : ""}`} />
          </button>

          {showArchived && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {deletedStudents.map((student) => (
                <Card
                  key={student.id}
                  className="p-5 opacity-60"
                  data-testid={`card-archived-student-${student.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-semibold text-sm flex-shrink-0">
                      {student.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate mb-1">{student.name}</h3>
                      <div className="flex flex-wrap items-center gap-1.5 mb-3">
                        <Badge variant="outline" className="text-muted-foreground">Archived</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmRestore(student)}
                          data-testid={`button-restore-${student.id}`}
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setConfirmPermDelete(student)}
                          data-testid={`button-perm-delete-${student.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmRestore}
        onOpenChange={(open) => !open && setConfirmRestore(null)}
        title="Restore Student?"
        description={`${confirmRestore?.name} will be restored to your active students list. Their schedule rules were cleared when archived — you'll need to set them up again.`}
        confirmLabel="Restore"
        variant="default"
        onConfirm={() => confirmRestore && restoreMutation.mutate(confirmRestore.id)}
        disabled={restoreMutation.isPending}
      />

      <ConfirmDialog
        open={!!confirmPermDelete}
        onOpenChange={(open) => !open && setConfirmPermDelete(null)}
        title="Permanently Delete Student?"
        description={`${confirmPermDelete?.name} will be permanently deleted along with all unpaid lessons. Paid lessons will be kept for your records. This cannot be undone.`}
        confirmLabel="Delete Forever"
        variant="destructive"
        onConfirm={() => confirmPermDelete && queuePermanentDelete(confirmPermDelete.id)}
        disabled={permanentDeleteMutation.isPending}
      />
      <UndoDeleteBanner
        open={!!pendingDeleteUi}
        message={pendingDeleteUi?.message || ""}
        deadline={pendingDeleteUi?.deadline || 0}
        durationMs={pendingDeleteUi?.durationMs || 10_000}
        onUndo={cancelPendingDelete}
      />
    </div>
  );
}
